import { IconRalphWiggum } from "@/assets/icons/IconRalphWiggum";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { AgentMode, cycleAgentMode, getAgentModeDescription, getAgentModeLabel } from "@/components/ai/prompt-input/agent-mode";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { CycleAgentModeKeyBinding, isMacLike } from "@/utils/platform";
import { IconCodeAssistant } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconCodeAssistant";
import { IconSearchlinesSparkle } from "@central-icons-react/round-outlined-radius-2-stroke-1.5/IconSearchlinesSparkle";
import { Check } from "lucide-react";
import { useEffect } from "react";

const getModeIcon = (mode: AgentMode, size: number) => {
    switch (mode) {
        case AgentMode.Ralph:
            return <IconRalphWiggum size={size} />;
        case AgentMode.Spec:
            return <IconSearchlinesSparkle size={size} className="text-content-brand-accent-01" />;
        case AgentMode.Agent:
        default:
            return <IconCodeAssistant size={size} className="text-content-brand" />;
    }
};

const getModeBgClass = (mode: AgentMode): string => {
    switch (mode) {
        case AgentMode.Ralph:
            return "bg-surface-orange";
        case AgentMode.Spec:
            return "bg-surface-brand-accent-01-subtle";
        case AgentMode.Agent:
        default:
            return "bg-surface-brand-subtle";
    }
};

export const AgentModeDropdown = ({
    mode,
    onModeChange,
    availableModes,
    disabled,
}: {
    mode: AgentMode;
    onModeChange: (mode: AgentMode) => void;
    availableModes: AgentMode[];
    disabled?: boolean;
}) => {
    // Handle Cmd/Ctrl+Shift+M to cycle agent modes
    useEffect(() => {
        if (disabled) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            const modifierKey = isMacLike() ? e.metaKey : e.ctrlKey;
            if (modifierKey && e.shiftKey && e.code === "KeyM") {
                e.preventDefault();
                e.stopPropagation();
                onModeChange(cycleAgentMode(mode, availableModes));
            }
        };

        document.addEventListener("keydown", handleKeyDown, true);
        return () => document.removeEventListener("keydown", handleKeyDown, true);
    }, [mode, onModeChange, availableModes, disabled]);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                disabled={disabled}
                className={cn(
                    "inline-flex h-8 min-w-24 items-center justify-between gap-1.5 rounded-md px-2 text-sm font-normal text-content-primary",
                    "hover:opacity-80 data-[state=open]:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-content-primary focus-visible:ring-offset-1",
                    disabled && "opacity-50 pointer-events-none",
                    getModeBgClass(mode),
                )}
            >
                <div className="flex items-center gap-1.5">
                    {getModeIcon(mode, 16)}
                    <span>{getAgentModeLabel(mode)}</span>
                </div>
                <IconChevronDownSmall size="sm" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="min-w-72">
                <div className="flex items-center justify-between px-2 py-1.5">
                    <DropdownMenuLabel className="p-0">Mode</DropdownMenuLabel>
                    <KeyboardCombo keys={CycleAgentModeKeyBinding} className="bg-surface-02" />
                </div>
                {availableModes.map((availableMode) => (
                    <DropdownMenuItem
                        key={availableMode}
                        onClick={() => onModeChange(availableMode)}
                        data-tracking-id={`agent-mode-${availableMode}`}
                        className="h-auto items-start py-1.5"
                    >
                        <div className="mt-0.5 shrink-0">{getModeIcon(availableMode, 16)}</div>
                        <div className="ml-2 flex min-w-0 flex-1 flex-col">
                            <span className="text-sm">{getAgentModeLabel(availableMode)}</span>
                            <span className="text-sm font-normal text-content-secondary">
                                {getAgentModeDescription(availableMode)}
                            </span>
                        </div>
                        {mode === availableMode && <Check size={14} className="ml-auto mt-0.5 shrink-0" />}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
